import type { EnqueueAction } from '../types/analytics';
import type { FlowHandle, StartFlowOptions } from '../types/analytics-client';
import type { ClockPort } from '../types/clock';
import type { FlowEventLogEntry, FlowHandleStaleAction } from './audit-types';
import { FlowNestedRejectedError } from './flow-nested-rejected-error';

type QueueMode = 'open' | 'flowActive' | 'gatedFlow';

type FlowManagerState = {
  flowId: string | null;
  gated: boolean;
};

type FlowManagerDependencies = {
  clock: ClockPort;
  onEventLogEntry?: (entry: FlowEventLogEntry) => void;
  onFlowEnded?: () => void;
  onGateReleased?: () => void;
};

export class FlowManager {
  private activeFlowId: string | null = null;
  private isGated = false;
  private activeFlowSource: string | null = null;
  private flowTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private hasFlowEnded = false;
  private latestFlowInstanceToken = 0;

  constructor(private readonly dependencies: FlowManagerDependencies) {}

  startFlow(opts: StartFlowOptions): FlowHandle {
    if (this.activeFlowId !== null) {
      this.dependencies.onEventLogEntry?.({
        type: 'flow-nested-rejected',
        meta: {
          activeFlowId: this.activeFlowId,
          attemptedFlowId: opts.id,
          source: opts.source,
        },
      });
      throw new FlowNestedRejectedError(
        `Nested analytics flow rejected. Flow ID: ${this.activeFlowId} attempted to start flow ID: ${opts.id}`
      );
    }

    const flowInstanceToken = ++this.latestFlowInstanceToken;
    this.activeFlowId = opts.id;
    this.isGated = false;
    this.activeFlowSource = opts.source;
    this.hasFlowEnded = false;
    this.armFlowTimeout(opts.timeoutMs);

    const isHandleStale = (action: FlowHandleStaleAction): boolean => {
      if (flowInstanceToken !== this.latestFlowInstanceToken || this.activeFlowId === null) {
        this.dependencies.onEventLogEntry?.({
          type: 'flow-handle-stale',
          meta: { action, flowId: opts.id },
        });
        return true;
      }
      return false;
    };

    return {
      id: opts.id,
      gate: () => {
        if (isHandleStale('gate')) {
          return;
        }
        this.gate();
      },
      releaseGate: () => {
        if (isHandleStale('releaseGate')) {
          return;
        }
        this.releaseGate();
      },
      end: () => {
        if (isHandleStale('end')) {
          return;
        }
        this.endFlow();
      },
    };
  }

  getFlowState(): FlowManagerState {
    return { flowId: this.activeFlowId, gated: this.isGated };
  }

  getQueueMode(): QueueMode {
    if (this.activeFlowId === null) {
      return 'open';
    }
    return this.isGated ? 'gatedFlow' : 'flowActive';
  }

  canDispatchNow(command: { flowId?: string }): boolean {
    const mode = this.getQueueMode();
    if (mode === 'open') {
      return true;
    }
    if (mode === 'gatedFlow') {
      return false;
    }
    return command.flowId === this.activeFlowId;
  }

  shouldHoldInPending(action: EnqueueAction): boolean {
    return this.getQueueMode() !== 'open' && !this.canDispatchNow({ flowId: action.flowId });
  }

  private gate(): void {
    if (this.isGated) {
      this.dependencies.onEventLogEntry?.({
        type: 'flow-gate-invalid',
        meta: { action: 'gate', reason: 'already-gated' },
      });
      return;
    }
    this.isGated = true;
  }

  private releaseGate(): void {
    if (!this.isGated) {
      this.dependencies.onEventLogEntry?.({
        type: 'flow-gate-invalid',
        meta: { action: 'releaseGate', reason: 'not-gated' },
      });
      return;
    }
    this.isGated = false;
    this.dependencies.onGateReleased?.();
  }

  private endFlow(): void {
    if (this.activeFlowId !== null) {
      this.finishFlow();
    }
  }

  private onFlowTimeout(): void {
    this.flowTimeoutHandle = null;
    if (this.activeFlowId === null) {
      return;
    }
    const meta = {
      flowId: this.activeFlowId,
      source: this.activeFlowSource ?? undefined,
      at: this.dependencies.clock.now(),
    };
    this.dependencies.onEventLogEntry?.({
      type: this.isGated ? 'flow-gate-timeout' : 'flow-timeout',
      meta,
    });
    this.finishFlow();
  }

  private finishFlow(): void {
    if (this.hasFlowEnded) {
      return;
    }
    this.hasFlowEnded = true;
    this.clearFlowTimeout();
    this.activeFlowId = null;
    this.isGated = false;
    this.activeFlowSource = null;
    this.dependencies.onFlowEnded?.();
  }

  private armFlowTimeout(timeoutMs: number): void {
    this.clearFlowTimeout();
    this.flowTimeoutHandle = setTimeout(() => {
      this.onFlowTimeout();
    }, timeoutMs);
  }

  private clearFlowTimeout(): void {
    if (this.flowTimeoutHandle !== null) {
      clearTimeout(this.flowTimeoutHandle);
      this.flowTimeoutHandle = null;
    }
  }
}
